import type { CanvasNode } from "@/types/visual-editor";
import {
  applyImageColorGrade,
  getImageColorGradeSignature,
  hasImageColorGrade,
  normalizeImageColorGrade,
  type NormalizedImageColorGrade,
} from "./image-color-grade";
export * from "./image-color-grade";
import {
  getImagePictureSignature,
  hasImagePicture,
  normalizeImagePicture,
  type NormalizedImagePicture,
  type PictureMask,
} from "./image-picture";
export * from "./image-picture";
import {
  normalizeImageRetouch,
  hasImageRetouch,
  getImageRetouchSignature,
  type NormalizedImageRetouch,
} from "./image-retouch";
export * from "./image-retouch";
import {
  normalizeImageFilter,
  hasImageFilter,
  applyImageFilter,
  type NormalizedImageFilter,
} from "./image-filters";
export * from "./image-filters";

export type ColorAdjust = NonNullable<CanvasNode["colorAdj"]>;

export const HSL_COLOR_BANDS = [
  {
    key: "red",
    labelKey: "hslBandRed",
    previewColor: "#ef4444",
    center: 0,
    width: 34,
  },
  {
    key: "orange",
    labelKey: "hslBandOrange",
    previewColor: "#f97316",
    center: 28,
    width: 24,
  },
  {
    key: "yellow",
    labelKey: "hslBandYellow",
    previewColor: "#eab308",
    center: 54,
    width: 24,
  },
  {
    key: "green",
    labelKey: "hslBandGreen",
    previewColor: "#22c55e",
    center: 120,
    width: 42,
  },
  {
    key: "aqua",
    labelKey: "hslBandAqua",
    previewColor: "#06b6d4",
    center: 185,
    width: 34,
  },
  {
    key: "blue",
    labelKey: "hslBandBlue",
    previewColor: "#3b82f6",
    center: 235,
    width: 42,
  },
  {
    key: "purple",
    labelKey: "hslBandPurple",
    previewColor: "#a855f7",
    center: 280,
    width: 32,
  },
  {
    key: "magenta",
    labelKey: "hslBandMagenta",
    previewColor: "#ec4899",
    center: 325,
    width: 32,
  },
] as const;

export type HslColorBandKey = (typeof HSL_COLOR_BANDS)[number]["key"];

export interface NormalizedHslBandAdjust {
  hue: number;
  saturation: number;
  lightness: number;
}

export type NormalizedHslBandAdjustments = Record<
  HslColorBandKey,
  NormalizedHslBandAdjust
>;
export type ColorAdjustmentGroup =
  "basic" | "hsl" | "curves" | "wheels" | "lut" | "effects";

export interface NormalizedColorAdjust {
  adjustmentMasks: PictureMask[];
  enabled: Record<ColorAdjustmentGroup, boolean>;
  picture: NormalizedImagePicture;
  retouch: NormalizedImageRetouch;
  filter: NormalizedImageFilter;
  grade: NormalizedImageColorGrade;
  exposure: number;
  brightness: number;
  contrast: number;
  highlights: number;
  shadows: number;
  whitePoint: number;
  blackPoint: number;
  saturate: number;
  temperature: number;
  hue: number;
  sepia: number;
  grayscale: number;
  hsl: NormalizedHslBandAdjustments;
}

export type ColorAdjustInput =
  ColorAdjust | NormalizedColorAdjust | null | undefined;

export const DEFAULT_HSL_BAND_ADJUST: NormalizedHslBandAdjust = {
  hue: 0,
  saturation: 0,
  lightness: 0,
};

export function createDefaultHslBandAdjustments(): NormalizedHslBandAdjustments {
  return Object.fromEntries(
    HSL_COLOR_BANDS.map(({ key }) => [key, { ...DEFAULT_HSL_BAND_ADJUST }]),
  ) as NormalizedHslBandAdjustments;
}

export const DEFAULT_COLOR_ADJUST: NormalizedColorAdjust = {
  adjustmentMasks: [],
  enabled: {
    basic: true,
    hsl: true,
    curves: true,
    wheels: true,
    lut: true,
    effects: true,
  },
  picture: normalizeImagePicture(),
  retouch: normalizeImageRetouch(),
  filter: normalizeImageFilter(),
  grade: normalizeImageColorGrade(),
  exposure: 0,
  brightness: 1,
  contrast: 1,
  highlights: 0,
  shadows: 0,
  whitePoint: 0,
  blackPoint: 0,
  saturate: 1,
  temperature: 0,
  hue: 0,
  sepia: 0,
  grayscale: 0,
  hsl: createDefaultHslBandAdjustments(),
};

function normalizeHslBandAdjustments(
  hsl?: ColorAdjust["hsl"] | null,
): NormalizedHslBandAdjustments {
  const normalized = createDefaultHslBandAdjustments();

  for (const { key } of HSL_COLOR_BANDS) {
    normalized[key] = {
      hue: hsl?.[key]?.hue ?? DEFAULT_HSL_BAND_ADJUST.hue,
      saturation: hsl?.[key]?.saturation ?? DEFAULT_HSL_BAND_ADJUST.saturation,
      lightness: hsl?.[key]?.lightness ?? DEFAULT_HSL_BAND_ADJUST.lightness,
    };
  }

  return normalized;
}

export function normalizeColorAdjust(
  colorAdj?: ColorAdjustInput,
): NormalizedColorAdjust {
  return {
    adjustmentMasks: normalizeImagePicture({ masks: colorAdj?.adjustmentMasks })
      .masks,
    enabled: Object.fromEntries(
      Object.keys(DEFAULT_COLOR_ADJUST.enabled).map((key) => [
        key,
        colorAdj?.enabled?.[key as ColorAdjustmentGroup] !== false,
      ]),
    ) as Record<ColorAdjustmentGroup, boolean>,
    picture: normalizeImagePicture(colorAdj?.picture),
    retouch: normalizeImageRetouch(colorAdj?.retouch),
    filter: normalizeImageFilter(colorAdj?.filter),
    grade: normalizeImageColorGrade(colorAdj?.grade),
    exposure: colorAdj?.exposure ?? DEFAULT_COLOR_ADJUST.exposure,
    brightness: colorAdj?.brightness ?? DEFAULT_COLOR_ADJUST.brightness,
    contrast: colorAdj?.contrast ?? DEFAULT_COLOR_ADJUST.contrast,
    highlights: colorAdj?.highlights ?? DEFAULT_COLOR_ADJUST.highlights,
    shadows: colorAdj?.shadows ?? DEFAULT_COLOR_ADJUST.shadows,
    whitePoint: colorAdj?.whitePoint ?? DEFAULT_COLOR_ADJUST.whitePoint,
    blackPoint: colorAdj?.blackPoint ?? DEFAULT_COLOR_ADJUST.blackPoint,
    saturate: colorAdj?.saturate ?? DEFAULT_COLOR_ADJUST.saturate,
    temperature: colorAdj?.temperature ?? DEFAULT_COLOR_ADJUST.temperature,
    hue: colorAdj?.hue ?? DEFAULT_COLOR_ADJUST.hue,
    sepia: colorAdj?.sepia ?? DEFAULT_COLOR_ADJUST.sepia,
    grayscale: colorAdj?.grayscale ?? DEFAULT_COLOR_ADJUST.grayscale,
    hsl: normalizeHslBandAdjustments(colorAdj?.hsl),
  };
}

/** 开关只屏蔽执行，不覆盖原配方中的滑块值，重新启用时可恢复。 */
export function effectiveColorAdjust(
  colorAdj?: ColorAdjustInput,
): NormalizedColorAdjust {
  const c = normalizeColorAdjust(colorAdj),
    d = DEFAULT_COLOR_ADJUST;
  if (!c.enabled.basic) {
    for (const key of [
      "exposure",
      "brightness",
      "contrast",
      "highlights",
      "shadows",
      "whitePoint",
      "blackPoint",
      "saturate",
      "temperature",
      "hue",
    ] as const)
      c[key] = d[key];
    c.grade.tint = 0;
    c.grade.vibrance = 0;
    c.grade.brilliance = 0;
  }
  if (!c.enabled.hsl) c.hsl = createDefaultHslBandAdjustments();
  if (!c.enabled.curves) c.grade.curves = d.grade.curves;
  if (!c.enabled.wheels) c.grade.wheelStrength = 0;
  if (!c.enabled.lut) c.grade.lut = null;
  if (!c.enabled.effects) {
    c.sepia = 0;
    c.grayscale = 0;
    for (const key of [
      "sharpen",
      "clarity",
      "grain",
      "fade",
      "vignette",
    ] as const)
      c.grade[key] = 0;
  }
  return c;
}

/** 批量复制调色时保留每张图的构图、背景和抠像；配方之间不共享可变蒙版/曲线。 */
export function copyImageColorAdjustments(
  source: ColorAdjustInput,
  target: ColorAdjustInput,
): NormalizedColorAdjust {
  return {
    ...normalizeColorAdjust(source),
    picture: normalizeImagePicture(target?.picture),
    retouch: normalizeImageRetouch(target?.retouch),
  };
}

function isHslBandAdjustDefault(
  adjust?: { hue?: number; saturation?: number; lightness?: number } | null,
): boolean {
  return (
    (adjust?.hue ?? 0) === DEFAULT_HSL_BAND_ADJUST.hue &&
    (adjust?.saturation ?? 0) === DEFAULT_HSL_BAND_ADJUST.saturation &&
    (adjust?.lightness ?? 0) === DEFAULT_HSL_BAND_ADJUST.lightness
  );
}

export function hasHslBandAdjustments(colorAdj?: ColorAdjustInput): boolean {
  const c = effectiveColorAdjust(colorAdj);
  return HSL_COLOR_BANDS.some(({ key }) => !isHslBandAdjustDefault(c.hsl[key]));
}

export function isColorAdjustDefault(colorAdj?: ColorAdjustInput): boolean {
  const c = effectiveColorAdjust(colorAdj);
  return (
    c.exposure === DEFAULT_COLOR_ADJUST.exposure &&
    c.highlights === DEFAULT_COLOR_ADJUST.highlights &&
    c.shadows === DEFAULT_COLOR_ADJUST.shadows &&
    c.whitePoint === DEFAULT_COLOR_ADJUST.whitePoint &&
    c.blackPoint === DEFAULT_COLOR_ADJUST.blackPoint &&
    c.brightness === DEFAULT_COLOR_ADJUST.brightness &&
    c.contrast === DEFAULT_COLOR_ADJUST.contrast &&
    c.saturate === DEFAULT_COLOR_ADJUST.saturate &&
    c.temperature === DEFAULT_COLOR_ADJUST.temperature &&
    c.hue === DEFAULT_COLOR_ADJUST.hue &&
    c.sepia === DEFAULT_COLOR_ADJUST.sepia &&
    c.grayscale === DEFAULT_COLOR_ADJUST.grayscale &&
    !hasImageColorGrade(c.grade) &&
    !hasImagePicture(c.picture) &&
    !hasImageRetouch(c.retouch) &&
    !hasImageFilter(c.filter) &&
    HSL_COLOR_BANDS.every(({ key }) => isHslBandAdjustDefault(c.hsl[key]))
  );
}

export function areColorAdjustEqual(
  a?: ColorAdjustInput,
  b?: ColorAdjustInput,
): boolean {
  const left = normalizeColorAdjust(a);
  const right = normalizeColorAdjust(b);
  return (
    left.exposure === right.exposure &&
    left.brightness === right.brightness &&
    left.contrast === right.contrast &&
    left.highlights === right.highlights &&
    left.shadows === right.shadows &&
    left.whitePoint === right.whitePoint &&
    left.blackPoint === right.blackPoint &&
    left.saturate === right.saturate &&
    left.temperature === right.temperature &&
    left.hue === right.hue &&
    left.sepia === right.sepia &&
    left.grayscale === right.grayscale &&
    getImageColorGradeSignature(left.grade) ===
      getImageColorGradeSignature(right.grade) &&
    getImagePictureSignature(left.picture) ===
      getImagePictureSignature(right.picture) &&
    getImageRetouchSignature(left.retouch) ===
      getImageRetouchSignature(right.retouch) &&
    JSON.stringify(left.filter) === JSON.stringify(right.filter) &&
    JSON.stringify(left.adjustmentMasks) ===
      JSON.stringify(right.adjustmentMasks) &&
    JSON.stringify(left.enabled) === JSON.stringify(right.enabled) &&
    HSL_COLOR_BANDS.every(
      ({ key }) =>
        left.hsl[key].hue === right.hsl[key].hue &&
        left.hsl[key].saturation === right.hsl[key].saturation &&
        left.hsl[key].lightness === right.hsl[key].lightness,
    )
  );
}

export function getColorAdjustSignature(colorAdj?: ColorAdjustInput): string {
  const c = normalizeColorAdjust(colorAdj);
  return [
    c.exposure,
    c.brightness,
    c.contrast,
    c.highlights,
    c.shadows,
    c.whitePoint,
    c.blackPoint,
    c.saturate,
    c.temperature,
    c.hue,
    c.sepia,
    c.grayscale,
    getImageColorGradeSignature(c.grade),
    getImagePictureSignature(c.picture),
    getImageRetouchSignature(c.retouch),
    JSON.stringify(c.filter),
    JSON.stringify(c.adjustmentMasks),
    JSON.stringify(c.enabled),
    ...HSL_COLOR_BANDS.flatMap(({ key }) => [
      c.hsl[key].hue,
      c.hsl[key].saturation,
      c.hsl[key].lightness,
    ]),
  ].join("|");
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

function smoothstep(edge0: number, edge1: number, value: number): number {
  if (edge0 === edge1) {
    return value < edge0 ? 0 : 1;
  }
  const t = clamp01((value - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

function applyWeightedRangeAdjustment(
  channel: number,
  amount: number,
  weight: number,
): number {
  const normalizedAmount = clamp01(Math.abs(amount) / 100) * clamp01(weight);
  if (normalizedAmount === 0) return channel;

  if (amount >= 0) {
    return channel + (1 - channel) * normalizedAmount;
  }

  return channel * (1 - normalizedAmount);
}

function clampSignedUnit(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value <= -1) return -1;
  if (value >= 1) return 1;
  return value;
}

function getHueDistanceDegrees(left: number, right: number): number {
  const rawDistance = Math.abs(left - right) % 360;
  return rawDistance > 180 ? 360 - rawDistance : rawDistance;
}

function getHslBandWeight(
  hueDegrees: number,
  saturation: number,
  band: (typeof HSL_COLOR_BANDS)[number],
): number {
  const hueDistance = getHueDistanceDegrees(hueDegrees, band.center);
  const hueWeight = 1 - smoothstep(band.width * 0.58, band.width, hueDistance);
  const chromaWeight = smoothstep(0.06, 0.22, saturation);
  return clamp01(hueWeight * chromaWeight);
}

function rgbToHsl(
  red: number,
  green: number,
  blue: number,
): [number, number, number] {
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  let hue = 0;
  let saturation = 0;
  const lightness = (max + min) / 2;

  if (max !== min) {
    const delta = max - min;
    saturation =
      lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case red:
        hue = (green - blue) / delta + (green < blue ? 6 : 0);
        break;
      case green:
        hue = (blue - red) / delta + 2;
        break;
      default:
        hue = (red - green) / delta + 4;
        break;
    }

    hue /= 6;
  }

  return [hue, saturation, lightness];
}

function hueToRgb(p: number, q: number, t: number): number {
  let nextT = t;
  if (nextT < 0) nextT += 1;
  if (nextT > 1) nextT -= 1;
  if (nextT < 1 / 6) return p + (q - p) * 6 * nextT;
  if (nextT < 1 / 2) return q;
  if (nextT < 2 / 3) return p + (q - p) * (2 / 3 - nextT) * 6;
  return p;
}

function hslToRgb(
  hue: number,
  saturation: number,
  lightness: number,
): [number, number, number] {
  if (saturation === 0) {
    return [lightness, lightness, lightness];
  }

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return [
    hueToRgb(p, q, hue + 1 / 3),
    hueToRgb(p, q, hue),
    hueToRgb(p, q, hue - 1 / 3),
  ];
}

export function applyColorAdjustToImageData(
  imageData: ImageData,
  colorAdj?: ColorAdjust | null,
): ImageData {
  const c = effectiveColorAdjust(colorAdj);
  if (
    isColorAdjustDefault({
      ...c,
      picture: DEFAULT_COLOR_ADJUST.picture,
      retouch: DEFAULT_COLOR_ADJUST.retouch,
    })
  ) {
    return imageData;
  }

  const data = imageData.data;
  const original = c.grade.mask.shape !== "none" ? data.slice() : undefined;
  const exposureFactor = 2 ** (c.exposure / 100);
  const temperatureShift = c.temperature / 100;
  const activeHslBands = HSL_COLOR_BANDS.map((band) => ({
    band,
    adjust: c.hsl[band.key],
  })).filter(({ adjust }) => !isHslBandAdjustDefault(adjust));

  for (let index = 0; index < data.length; index += 4) {
    if (data[index + 3] === 0) continue;

    let red = data[index] / 255;
    let green = data[index + 1] / 255;
    let blue = data[index + 2] / 255;

    red = clamp01(red * exposureFactor * c.brightness);
    green = clamp01(green * exposureFactor * c.brightness);
    blue = clamp01(blue * exposureFactor * c.brightness);

    const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    const shadowWeight = 1 - smoothstep(0.18, 0.72, luminance);
    const highlightWeight = smoothstep(0.28, 0.92, luminance);
    const blackWeight = 1 - smoothstep(0.02, 0.35, luminance);
    const whiteWeight = smoothstep(0.65, 0.98, luminance);

    red = applyWeightedRangeAdjustment(red, c.shadows, shadowWeight);
    green = applyWeightedRangeAdjustment(green, c.shadows, shadowWeight);
    blue = applyWeightedRangeAdjustment(blue, c.shadows, shadowWeight);

    red = applyWeightedRangeAdjustment(red, c.highlights, highlightWeight);
    green = applyWeightedRangeAdjustment(green, c.highlights, highlightWeight);
    blue = applyWeightedRangeAdjustment(blue, c.highlights, highlightWeight);

    red = applyWeightedRangeAdjustment(red, c.blackPoint, blackWeight);
    green = applyWeightedRangeAdjustment(green, c.blackPoint, blackWeight);
    blue = applyWeightedRangeAdjustment(blue, c.blackPoint, blackWeight);

    red = applyWeightedRangeAdjustment(red, c.whitePoint, whiteWeight);
    green = applyWeightedRangeAdjustment(green, c.whitePoint, whiteWeight);
    blue = applyWeightedRangeAdjustment(blue, c.whitePoint, whiteWeight);

    red = clamp01((red - 0.5) * c.contrast + 0.5);
    green = clamp01((green - 0.5) * c.contrast + 0.5);
    blue = clamp01((blue - 0.5) * c.contrast + 0.5);

    red = clamp01(red + temperatureShift * 0.08);
    green = clamp01(green + temperatureShift * 0.02);
    blue = clamp01(blue - temperatureShift * 0.08);

    let [hue, saturation, lightness] = rgbToHsl(red, green, blue);
    hue = (hue + c.hue / 360 + 1) % 1;
    saturation = clamp01(saturation * c.saturate);

    for (const { band, adjust } of activeHslBands) {
      const bandWeight = getHslBandWeight(hue * 360, saturation, band);
      if (bandWeight <= 0) {
        continue;
      }

      hue = (hue + (adjust.hue * bandWeight) / 360 + 1) % 1;
      saturation = clamp01(
        saturation *
          (1 + clampSignedUnit(adjust.saturation / 100) * bandWeight),
      );
      lightness = clamp01(
        lightness + clampSignedUnit(adjust.lightness / 100) * 0.35 * bandWeight,
      );
    }

    [red, green, blue] = hslToRgb(hue, saturation, lightness);

    if (c.sepia > 0) {
      const sepiaRed = clamp01(red * 0.393 + green * 0.769 + blue * 0.189);
      const sepiaGreen = clamp01(red * 0.349 + green * 0.686 + blue * 0.168);
      const sepiaBlue = clamp01(red * 0.272 + green * 0.534 + blue * 0.131);
      red += (sepiaRed - red) * c.sepia;
      green += (sepiaGreen - green) * c.sepia;
      blue += (sepiaBlue - blue) * c.sepia;
    }

    if (c.grayscale > 0) {
      const grayscale = 0.2126 * red + 0.7152 * green + 0.0722 * blue;
      red += (grayscale - red) * c.grayscale;
      green += (grayscale - green) * c.grayscale;
      blue += (grayscale - blue) * c.grayscale;
    }

    data[index] = Math.round(clamp01(red) * 255);
    data[index + 1] = Math.round(clamp01(green) * 255);
    data[index + 2] = Math.round(clamp01(blue) * 255);
  }

  applyImageColorGrade(imageData, c.grade, original);
  applyImageFilter(imageData, c.filter);
  return imageData;
}

export function buildColorAdjustCssFilter(
  colorAdj?: ColorAdjust | null,
): string {
  const c = effectiveColorAdjust(colorAdj);
  const parts: string[] = [];
  const hueRotate = c.hue;
  const exposureBrightness = 2 ** (c.exposure / 100);

  if (
    c.brightness !== DEFAULT_COLOR_ADJUST.brightness ||
    c.exposure !== DEFAULT_COLOR_ADJUST.exposure
  ) {
    parts.push(`brightness(${c.brightness * exposureBrightness})`);
  }
  if (c.contrast !== DEFAULT_COLOR_ADJUST.contrast)
    parts.push(`contrast(${c.contrast})`);
  if (c.saturate !== DEFAULT_COLOR_ADJUST.saturate)
    parts.push(`saturate(${c.saturate})`);
  if (hueRotate !== 0) parts.push(`hue-rotate(${hueRotate}deg)`);
  if (c.sepia !== DEFAULT_COLOR_ADJUST.sepia) parts.push(`sepia(${c.sepia})`);
  if (c.grayscale !== DEFAULT_COLOR_ADJUST.grayscale)
    parts.push(`grayscale(${c.grayscale})`);

  return parts.length ? parts.join(" ") : "none";
}
